import type { PortalConfig } from 'context/PortalConfigContext'

type PortalDomains = Pick<PortalConfig, 'domain' | 'epilot_domain'>

/**
 * Bare host of a configured domain, which may or may not carry a scheme or a
 * path. `www.` is dropped to match how the API normalises domains before
 * looking a portal up.
 */
export const toPortalHost = (domain: string): string | undefined => {
  try {
    return new URL(
      domain.includes('://') ? domain : `https://${domain}`
    ).hostname
      .toLowerCase()
      .replace(/^www\./, '')
  } catch {
    return undefined
  }
}

const currentHost = (): string =>
  window.location.hostname.toLowerCase().replace(/^www\./, '')

/** The portal's own domains, custom one first, as the API reports them. */
const portalDomains = (
  portalConfig: PortalDomains | undefined | null
): string[] =>
  [portalConfig?.domain, portalConfig?.epilot_domain].filter(
    (domain): domain is string => !!domain
  )

/**
 * A portal is reachable on two domains at once: the custom one an org brings
 * (`domain`) and the built-in one epilot provisions (`epilot_domain`, e.g.
 * `<portal>.ecp.epilot.cloud`). Both are indexed server-side, so either one
 * resolves the same portal.
 *
 * The value is not only a lookup key though - the API derives URIs from it
 * that are handed back to the browser, notably the SSO callback's
 * `redirect_uri`. Reporting the custom domain to a visitor who loaded the
 * built-in one therefore sends them across domains mid-flow, away from the
 * origin that holds their login state.
 *
 * So report whichever of the portal's domains is actually in use, and fall
 * back to the configured order only when the current host is neither of them -
 * inside the mobile app shell, the Portal Builder preview iframe or on
 * localhost, where `window.location` says nothing about the portal's domain.
 */
export const getPortalDomain = (
  portalConfig: PortalDomains | undefined | null
): string | undefined => {
  const domains = portalDomains(portalConfig)
  const host = currentHost()

  return domains.find((domain) => toPortalHost(domain) === host) ?? domains[0]
}

let knownPortalHosts: readonly string[] = []

/**
 * Records the portal's own domains for the SSO code that runs outside React
 * and so cannot reach `PortalConfigContext` - logout, which has to tell a URI
 * pointing at this portal's other domain apart from one pointing at an org's
 * own signed-out page.
 */
export const rememberPortalDomains = (
  portalConfig: PortalDomains | undefined | null
): void => {
  knownPortalHosts = portalDomains(portalConfig)
    .map(toPortalHost)
    .filter((host): host is string => !!host)
}

/**
 * Whether `host` is one of the domains this portal is served on. Answers
 * `false` until the portal config has resolved, which keeps every caller on
 * its configured value rather than guessing.
 */
export const isPortalHost = (host: string | undefined): boolean =>
  !!host && knownPortalHosts.includes(host)

/**
 * The origin to build portal URLs on. `window.location` is authoritative on
 * the web - it names the domain the visitor picked - but inside the mobile app
 * shell it carries a native scheme (`capacitor://localhost`) that no IdP can
 * redirect to, so fall back to the portal's own domain there.
 */
export const getPortalOrigin = (): string => {
  const { origin, protocol } = window.location

  if (protocol === 'http:' || protocol === 'https:') return origin

  return knownPortalHosts.length ? `https://${knownPortalHosts[0]}` : origin
}
